import React from 'react';
import { useDispatch } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import { useParams } from 'react-router-dom';
import MainContainer from '../../components/MainContainer';
import Loading from '../../components/Loading';
import Unsubscribe from '../../components/Verification/Unsubscribe';
import { verificationAccessScreen } from '../../components/Verification/VerificationAccessScreen';
import { unsubscribeVerification } from '../../actions';
import useVerification from '../../api/verifications/useVerification';
import { useAuthStatus } from '../../utils/useAuthReady';
import useVerificationAccessRetry from '../../utils/useVerificationAccessRetry';

const Subscription: React.FC = () => {
  const { loading: authLoading, isLoggedIn } = useAuthStatus();
  const { verificationId } = useParams();
  const {
    verification,
    fetching,
    errors,
    access,
    fetch: fetchVerification,
    syncFromStore,
  } = useVerification(verificationId);
  const { waitingToRetry, retryNow, markRetried } = useVerificationAccessRetry({
    access,
    errors,
    authLoading,
    verificationId,
  });
  const dispatch = useDispatch();
  React.useEffect(() => {
    document.title = i18n.getStr('containers.page.manageSubscription');
  }, []);

  React.useEffect(() => {
    if (fetching || verification || (access && !retryNow)) return;
    if (access) markRetried();
    fetchVerification();
  }, [
    fetchVerification,
    fetching,
    verification,
    access,
    retryNow,
    markRetried,
  ]);

  const handleUnsubscribe = async () => {
    await dispatch<any>(unsubscribeVerification(verification));
    // The thunk stores the unsubscribed verification; show it.
    syncFromStore();
  };

  if (fetching || waitingToRetry || retryNow) {
    return (
      <MainContainer>
        <Loading />
      </MainContainer>
    );
  }

  const accessScreen =
    !verification &&
    verificationAccessScreen({ access, verificationId, isLoggedIn });
  if (accessScreen) {
    return <MainContainer>{accessScreen}</MainContainer>;
  }

  if (!verification) {
    return null;
  }

  return (
    <MainContainer>
      <Unsubscribe verification={verification} onSubmit={handleUnsubscribe} />
    </MainContainer>
  );
};

export default i18n.renderTranslation()(Subscription);
